<template>
  <div class="w-full text-sm">
    <!-- Header -->
    <div class="h-[40px] flex items-center gap-[0.75rem] bg-[#F4F4F4] rounded-[6px] mb-[20px] text-[#0A0A0A] font-medium border border-[#E5E5E5]">
      <div
        v-for="header in config.headers"
        :key="header.key"
        :class="header.class"
      >
        <b-form-checkbox
          v-if="header.key === 'checkbox'"
          :checked="selectAll"
          @change="toggleSelectAll"
        />
        <template v-else>
          {{ header.label }}
        </template>
      </div>
    </div>

    <!-- Grouped Lists -->
    <div class="flex flex-col gap-4">
      <article-group
        v-for="(group, groupIndex) in groups"
        :key="group.category_id"
        ref="artikelGroups"
        :group="group"
        :config="config"
        :selected-map="selectedMap"
        @edit="$emit('edit', $event)"
        @delete="$emit('delete', { ...$event, groupIndex })"
        @group-selection="handleGroupSelection"
        @load-more="$emit('load-more', { ...$event, group_index: groupIndex })"
        @toggle-select="handleToggleSelect($event, groupIndex)"
        @toggle-select-batch="handleToggleSelectBatch($event, groupIndex)"
      />
    </div>
  </div>
</template>

<script>
import { ARTIKEL_CONFIG } from './config'
import ArticleGroup from './ArticleGroup.vue'

export default {
  name: 'ArtikelList',
  components: {
    ArticleGroup,
  },
  props: {
    groups: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      config: ARTIKEL_CONFIG,
      selectAll: false,
      selectedGroupsMap: {},
      /** Reactive map keyed by article ID: { [id]: { value: boolean, setLoading: fn|null, groupIndex: number, articleIndex?: number } } */
      selectedMap: {},
      /** Counter maintained in sync with selectedMap — avoids deep watcher scan */
      selectedCount: 0,
    }
  },
  computed: {
    anyArticles() {
      return this.groups.some(comp => comp.articles && comp.articles.length > 0)
    },
  },
  watch: {
    groups() {
      this.checkGlobalSelectAll()
    },
  },
  methods: {
    /**
     * Selects or deselects all articles across every group.
     * Uses a delta counter to update selectedCount in a single operation.
     * @param {boolean} val - Target selection state
     */
    toggleSelectAll(val) {
      let delta = 0
      this.groups.forEach((group, groupIndex) => {
        if (group.articles) {
          group.articles.forEach((item, articleIndex) => {
            const prev = !!this.selectedMap[item.id]?.value
            if (prev !== val) {
              const groupRef = this.$refs.artikelGroups?.[groupIndex]
              this.$set(this.selectedMap, item.id, {
                id: item.id,
                value: val,
                title: item.title,
                groupIndex,
                setLoading: groupRef
                  ? (v, type, status) => groupRef.setLoadingItem({
                    value: v, type, status, id: item.id,
                  })
                  : null,
              })
              delta += val ? 1 : -1
            }
          })
        }
      })
      this.selectedCount += delta
      this.$emit('selection-change', this.selectedCount)
    },
    /**
     * Handles a single article toggle-select event emitted by ArticleGroup.
     * Skips the update when the value has not actually changed.
     * @param {{ id: string|number, value: boolean, setLoading: Function }} payload
     * @param {number} groupIndex
     */
    handleToggleSelect(payload, groupIndex) {
      const { value, id } = payload
      const prev = !!this.selectedMap[id]?.value
      if (prev === value) return
      this.$set(this.selectedMap, id, {
        ...payload, groupIndex,
      })
      this.selectedCount += value ? 1 : -1
      this.$emit('selection-change', this.selectedCount)
      this.checkGlobalSelectAll()
    },
    /** Batch update for group-level select/deselect — single emit instead of N */
    handleToggleSelectBatch({ items, value }, groupIndex) {
      let delta = 0
      items.forEach(payload => {
        const { id } = payload
        const prev = !!this.selectedMap[id]?.value
        if (prev !== value) {
          this.$set(this.selectedMap, id, { ...payload, value, groupIndex })
          delta += value ? 1 : -1
        }
      })
      this.selectedCount += delta
      this.$emit('selection-change', this.selectedCount)
      this.checkGlobalSelectAll()
    },
    handleGroupSelection(isFullySelected, groupId) {
      this.$set(this.selectedGroupsMap, groupId, isFullySelected)
      this.checkGlobalSelectAll()
    },
    /**
     * Re-evaluates the global "select all" checkbox state.
     * Marks selectAll as true only when every non-empty group is fully selected.
     */
    checkGlobalSelectAll() {
      let allSelected = true

      if (this.anyArticles) {
        allSelected = this.groups.every(comp => {
          if (comp.articles && comp.articles.length > 0) {
            return !!this.selectedGroupsMap[comp.category_id]
          }
          return true
        })
      }

      if (this.anyArticles && allSelected !== this.selectAll) {
        this.selectAll = allSelected
      } else if (!this.anyArticles && this.selectAll) {
        this.selectAll = false
      }
    },
    /**
     * Returns all entries from selectedMap where value is true.
     * @returns {Array<[string, { value: boolean, title: string, setLoading: Function, groupIndex: number }]>}
     */
    getSelectedItems() {
      return Object.entries(this.selectedMap).filter(([, { value }]) => value)
    },
    /**
     * Resets all selection state back to the initial empty state.
     * Also emits 'selection-change' with 0 to notify the parent.
     */
    resetSelection() {
      this.selectedMap = {}
      this.selectedCount = 0
      this.selectAll = false
      this.selectedGroupsMap = {}
      if (this.$refs.artikelGroups) {
        this.$refs.artikelGroups.forEach(groupRef => {
          if (groupRef) groupRef.setCheckboxLoading(false)
        })
      }
      this.$emit('selection-change', 0)
    },
  },
}
</script>
